import { setWithExpiry } from "../../utils/localCache";

const ITEMS_PER_PAGE = 24;
const CACHE_EXPIRY_MS_SHORT = 10000;
const REQUEST_TIMEOUT_MS = 10000;
const searchPath = "/api/v1/posts";

/**
 * Post data fetching logic for PostIndexCollection.
 * Manages API requests, pagination, error states, and request deduplication.
 *
 * @param {object} vm - Vue component instance (Options API)
 * @returns {{ fetchInitialData, fetchItemData, retryInitialLoad, retryLoad, fetchPopularTagsData, ITEMS_PER_PAGE }}
 */
export function usePostFetcher(vm) {
  function trackViewItemList(itemsData, pageNumber) {
    if (!window.trackingEvent || !itemsData?.length) return;

    const raw = vm.currentListName || { id: "post_index", name: "post_index" };
    const listId = typeof raw === "object" ? raw.id : raw;
    const listName = typeof raw === "object" ? raw.name : raw;
    const baseIndex = (pageNumber - 1) * ITEMS_PER_PAGE;

    window.trackingEvent.view_item_list({
      item_list_id: listId,
      item_list_name: listName,
      item_list_index: pageNumber,
      items: itemsData.map((item, index) => ({
        id: item.id,
        title: item.title,
        creator: item.creator?.brand_name,
        price: item.price,
        index: baseIndex + index,
      })),
    });
  }

  async function fetchItemData(context = "pagination") {
    const targetPage = vm.currPage + 1;

    if (vm.fetchingPages.has(targetPage)) return;
    vm.fetchingPages.add(targetPage);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const originalCurrPage = vm.currPage;

    try {
      vm.loading = true;
      vm.itemsNotFound = false;
      vm.hasErrorOnLoad = false;

      const requestParams = { ...vm.currentParams };
      requestParams["per_page"] = ITEMS_PER_PAGE;
      requestParams["page"] = targetPage;

      const response = await fetch(
        searchPath + "?" + new URLSearchParams(requestParams),
        { signal: controller.signal },
      );
      clearTimeout(timeoutId);

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const itemsData = data["data"];

      // 把該次搜尋的 Algolia queryID 掛到每筆 item，供點擊時送 Insights click 事件
      const queryId = data["query_id"];
      if (queryId && Array.isArray(itemsData)) {
        itemsData.forEach((item) => {
          item.__queryID = queryId;
        });
      }

      vm.isLoggedIn = data.is_authenticated;

      if (requestParams["page"] > data["pagination"].total_pages) {
        vm.autoload = false;
      } else if (itemsData.length > 0) {
        vm.items.push(...itemsData);
        vm.currPage = targetPage;

        trackViewItemList(itemsData, targetPage);

        setWithExpiry(
          vm.cacheKey,
          {
            params: vm.currentQueryString,
            data: vm.items,
            page: vm.currPage,
          },
          CACHE_EXPIRY_MS_SHORT,
        );
      }

      if (vm.items.length === 0) {
        vm.autoload = false;
        vm.itemsNotFound = true;
        const contentTitle = document.getElementById("post_index_title");
        if (contentTitle) contentTitle.classList.add("hidden");
        const profileCollection = document.getElementById(
          "profile_index_collection",
        );
        if (profileCollection) profileCollection.classList.add("hidden");
        fetchPopularTagsData();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      vm.currPage = originalCurrPage;

      if (context === "initial") {
        throw error;
      }

      vm.hasErrorOnLoad = true;
      if (error.name === "AbortError") {
        vm.lastErrorMessage =
          vm.$t("errors.request_timeout") ||
          "Request timeout. Please try again.";
      } else if (
        error.message.includes("502") ||
        error.message.includes("500")
      ) {
        vm.lastErrorMessage =
          vm.$t("errors.server_error") ||
          "Server error. Please try again later.";
      } else {
        vm.lastErrorMessage =
          vm.$t("errors.failed_to_load_posts") ||
          "Failed to load posts. Please try again.";
      }

      if (vm.items.length === 0) vm.autoload = false;
    } finally {
      vm.fetchingPages.delete(targetPage);
      vm.loading = false;
    }
  }

  async function fetchInitialData() {
    try {
      await fetchItemData("initial");
      vm.initialLoadComplete = true;
    } catch (error) {
      console.error("Initial load failed:", error);
      vm.initialLoadComplete = true;
      vm.initialLoadFailed = true;

      if (error.name === "AbortError") {
        vm.initialErrorMessage =
          vm.$t("errors.request_timeout") ||
          "Request timeout. Please try again.";
      } else if (
        error.message.includes("502") ||
        error.message.includes("500")
      ) {
        vm.initialErrorMessage =
          vm.$t("errors.server_error") ||
          "Server error. Please try again later.";
      } else {
        vm.initialErrorMessage =
          vm.$t("errors.initial_load_failed") ||
          "Failed to load content. Please try again.";
      }
    }
  }

  function retryInitialLoad() {
    vm.initialLoadFailed = false;
    vm.initialErrorMessage = "";
    vm.items = [];
    vm.currPage = 0;
    vm.autoload = true;
    vm.fetchingPages = new Set();
    vm.loading = true;
    fetchInitialData();
  }

  function retryLoad() {
    vm.hasErrorOnLoad = false;
    vm.lastErrorMessage = "";
    vm.autoload = true;
    vm.fetchingPages = new Set();
    fetchItemData();
  }

  async function fetchPopularTagsData() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch("/api/v1/posts/recommand_keywords", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data["data"].length > 0) vm.popularTags = data["data"];
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Failed to fetch popular tags:", error);
    }
  }

  return {
    fetchInitialData,
    fetchItemData,
    retryInitialLoad,
    retryLoad,
    fetchPopularTagsData,
    ITEMS_PER_PAGE,
  };
}
